<template>
  <div class="oplog">
    <EmpExportInfoModal
      modal-id="oplog-export-limit-modal"
      v-model="exportLimitModalVisible"
      :title="$t('lbl_EMPBE_OpLog_ExportLimitModalTitle')"
      :subtitle="exportLimitModalSubtitle"
      :ok-text="$t('lbl_EMPBE_OpLog_ExportLimitModalOk')"
    />
    <!-- 導出失敗／逾時／請求錯誤：居中彈窗（黑色右下角僅保留「下載進行中」提示） -->
    <EmpExportInfoModal
      modal-id="oplog-export-fail-modal"
      v-model="exportFailureModalVisible"
      :title="$t('lbl_EMPBE_OpLog_ExportFailTitle')"
      :subtitle="$t('lbl_EMPBE_OpLog_ExportFailSubtitle')"
      :ok-text="$t('lbl_EMPBE_OpLog_ExportFailModalOk')"
    />
    <!-- 僅在全站已有導出任務時，用戶在本頁點擊「導出」才打開；後端衝突類回應亦走此彈窗 -->
    <EmpExportInfoModal
      modal-id="oplog-export-queue-modal"
      v-model="exportQueueModalVisible"
      :title="$t('lbl_EMPBE_OpLog_ExportQueueModalTitle')"
      :subtitle="$t('lbl_EMPBE_OpLog_ExportQueueModalSubtitle')"
      :ok-text="$t('lbl_EMPBE_OpLog_ExportQueueModalBtnOk')"
    />
    <b-overlay :show="loading || resettingFilters" rounded="sm">
      <div class="oplog__header" v-if="companyDisplayName">
        <div class="oplog__company">
          <span class="oplog__company-name">{{ companyDisplayName }}</span>
          <span class="oplog__company-id">({{ companyId }})</span>
        </div>
      </div>
      <div class="oplog__filters">
        <div class="row">
          <div class="col-12 col-md-3 mb-2 oplog__filter-field">
            <label class="oplog__label">{{ $t("lbl_EMPBE_OpLog_LogType") }}</label>
            <b-form-select
              v-model="mode"
              class="oplog__filter-control"
              :options="modeOptions"
              size="sm"
              @change="onLogTypeChange"
            />
          </div>
          <div class="col-12 col-md-3 mb-2 oplog__filter-field">
            <label class="oplog__label">{{ $t("lbl_EMPBE_OpLog_User") }}</label>
            <b-dropdown
              class="oplog__user-filter-dropdown oplog__filter-control"
              variant="outline-secondary"
              size="sm"
              block
              right
              menu-class="oplog__user-filter-menu"
            >
              <template #button-content>
                <span
                  class="oplog__user-filter-btn-label"
                  v-html="userFilterButtonHtml"
                />
              </template>
              <b-dropdown-item
                :active="!filters.actionByUser"
                @click="filters.actionByUser = ''"
              >
                {{ $t("lbl_EMPBE_OpLog_AllUsers") }}
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item
                v-for="opt in userFilterMenuOptions"
                :key="opt.value"
                :active="String(filters.actionByUser) === String(opt.value)"
                @click="filters.actionByUser = opt.value"
              >
                <span class="oplog__user-filter-item-text" v-html="opt.html" />
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div class="col-12 col-md-3 mb-2 oplog__filter-field">
            <label class="oplog__label">{{ $t("lbl_EMPBE_OpLog_Action") }}</label>
            <b-form-select
              v-model="filters.action"
              class="oplog__filter-control"
              :options="actionOptions"
              size="sm"
            />
          </div>
          <div class="col-12 col-md-3 mb-2 oplog__filter-field">
            <label class="oplog__label">{{ typeFilterLabel }}</label>
            <b-form-select
              v-model="filters.formType"
              class="oplog__filter-control"
              :options="typeFilterOptions"
              size="sm"
            />
          </div>
          <div class="col-12 col-md-3 mb-2 oplog__filter-field">
            <label class="oplog__label">{{ $t("lbl_EMPBE_OpLog_FromDate") }}</label>
            <b-form-input
              v-model="filters.fromDate"
              class="oplog__filter-control"
              type="date"
              size="sm"
            />
          </div>
          <div class="col-12 col-md-3 mb-2 oplog__filter-field">
            <label class="oplog__label">{{ $t("lbl_EMPBE_OpLog_ToDate") }}</label>
            <b-form-input
              v-model="filters.toDate"
              class="oplog__filter-control"
              type="date"
              size="sm"
            />
          </div>
          <div
            class="col-12 col-md-6 mb-2 d-flex align-items-end flex-nowrap overflow-x-auto oplog__filter-actions-bar"
          >
            <button
              class="btn btn-primary btn-sm mr-2 mb-1 flex-shrink-0"
              type="button"
              :disabled="loading || resettingFilters"
              @click="runSearch"
            >
              {{ $t("lbl_EMPBE_OpLog_Search") }}
            </button>
            <button
              type="button"
              class="btn btn-sm mr-2 mb-1 emp-dl-export-btn flex-shrink-0"
              :class="exportActionButtonClass"
              :disabled="!canQuery || downloading || resettingFilters"
              :aria-busy="downloading ? 'true' : 'false'"
              :title="exportButtonTitleAttr"
              @click="startExportDownload"
            >
              <span v-if="downloading" class="emp-dl-export-btn__spinner" aria-hidden="true"></span>
              {{ exportButtonLabel }}
            </button>
            <div class="d-inline-flex align-items-center flex-nowrap mb-1 flex-shrink-0 oplog__filter-actions-tail">
              <button
                class="btn btn-outline-secondary btn-sm mr-2 flex-shrink-0"
                type="button"
                :disabled="loading || downloading || resettingFilters"
                @click="resetFilters"
              >
                {{ $t("lbl_EMPBE_OpLog_Reset") }}
              </button>
            </div>
          </div>
        </div>
        <div class="oplog__hint" v-if="hint">
          {{ hint }}
        </div>
      </div>

      <b-table
        striped
        hover
        small
        responsive="md"
        table-class="oplog__table"
        no-local-sorting
        :items="items"
        :fields="tableFields"
        :empty-text="emptyText"
        @row-clicked="openDetail"
      >
        <template #cell(createdDate)="data">
          <span>{{ formatDate(data.item.createdDate) }}</span>
        </template>
        <template #cell(formData)="data" v-if="mode === 'FORM'">
          <span class="oplog__json-preview">{{ previewJson(data.item.formData) }}</span>
        </template>
        <template #cell(detailPreview)="data" v-if="mode === 'USER_ACTION'">
          <span class="oplog__json-preview">{{ userActionRowPreview(data.item) }}</span>
        </template>
        <template #cell(actionByUser)="data">
          <span
            class="oplog__operator"
            :title="operatorAuditTooltip(data.item)"
            v-html="operatorCellHtml(data.item)"
          />
        </template>
        <template #cell(formType)="data">
          <span>{{ formTypeColumnDisplayLabel(data.item) }}</span>
        </template>
        <template #cell(action)="data">
          <span>{{ actionColumnDisplayLabel(data.item) }}</span>
        </template>
      </b-table>

      <div class="oplog__pager">
        <b-pagination
          v-model="page"
          :total-rows="total"
          :per-page="pageSize"
          align="center"
          @change="onPageChange"
        />
      </div>
    </b-overlay>

    <b-modal
      id="oplog-detail-modal"
      v-model="detailModalVisible"
      :title="$t('lbl_EMPBE_OpLog_Detail')"
      size="lg"
      hide-footer
      scrollable
      body-class="oplog-detail-modal-body"
    >
      <div v-if="selected">
        <template v-if="detailMode === 'USER_ACTION'">
          <div class="row mb-2">
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_Field_Rid") }}</strong>：{{ selected.rid }}</div>
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_Field_UserId") }}</strong>：{{ operatorFullLabel(selected) }}</div>
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_Action") }}</strong>：{{ detailActionDisplayLabel() }}</div>
          </div>
          <div class="row mb-2">
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_LogActionType") }}</strong>：{{ detailFormTypeDisplayLabel() }}</div>
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_Field_EmpCompanyId") }}</strong>：{{ selected.createdBy }}</div>
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_CreatedDate") }}</strong>：{{ formatDate(selected.createdDate) }}</div>
          </div>
          <div class="row mb-2" v-if="selected.url">
            <div class="col-12"><strong>{{ $t("lbl_EMPBE_OpLog_Field_Url") }}</strong>：<a :href="selected.url" target="_blank" rel="noopener noreferrer">{{ selected.url }}</a></div>
          </div>
          <div class="row mb-2" v-if="selected.referral">
            <div class="col-12"><strong>{{ $t("lbl_EMPBE_OpLog_Field_Referral") }}</strong>：{{ selected.referral }}</div>
          </div>
          <div class="row mb-2" v-if="selected.ip || selected.bAgent">
            <div class="col-12 col-md-6" v-if="selected.ip"><strong>{{ $t("lbl_EMPBE_OpLog_Field_Ip") }}</strong>：{{ selected.ip }}</div>
            <div class="col-12 col-md-6" v-if="selected.bAgent"><strong>{{ $t("lbl_EMPBE_OpLog_Field_UserAgent") }}</strong>：{{ selected.bAgent }}</div>
          </div>
          <div class="row mb-2" v-if="selected.domId || selected.domClass">
            <div class="col-12 col-md-6" v-if="selected.domId"><strong>{{ $t("lbl_EMPBE_OpLog_Field_DomId") }}</strong>：{{ selected.domId }}</div>
            <div class="col-12 col-md-6" v-if="selected.domClass"><strong>{{ $t("lbl_EMPBE_OpLog_Field_DomClass") }}</strong>：{{ selected.domClass }}</div>
          </div>
        </template>
        <template v-else>
          <div class="row mb-2">
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_Field_Rid") }}</strong>：{{ selected.rid }}</div>
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_Field_FormId") }}</strong>：{{ selected.formId }}</div>
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_Action") }}</strong>：{{ detailActionDisplayLabel() }}</div>
          </div>
          <div class="row mb-2">
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_FormType") }}</strong>：{{ detailFormTypeDisplayLabel() }}</div>
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_Field_CreatedBy") }}</strong>：{{ selected.createdBy }}</div>
            <div class="col-12 col-md-4"><strong>{{ $t("lbl_EMPBE_OpLog_Field_ActionByUser") }}</strong>：{{ operatorFullLabel(selected) }}</div>
          </div>
          <div class="row mb-2">
            <div class="col-12"><strong>{{ $t("lbl_EMPBE_OpLog_CreatedDate") }}</strong>：{{ formatDate(selected.createdDate) }}</div>
          </div>
          <div class="row mb-2" v-if="selected.errorLog">
            <div class="col-12"><strong>{{ $t("lbl_EMPBE_OpLog_Field_ErrorLog") }}</strong>：{{ selected.errorLog }}</div>
          </div>
          <div class="row">
            <div class="col-12">
              <strong>{{ $t("lbl_EMPBE_OpLog_FormData") }}</strong>
              <pre class="oplog__json">{{ prettyJson(selected.formData) }}</pre>
            </div>
          </div>
        </template>
      </div>
    </b-modal>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import EmpExportInfoModal from "@/components/empDownloadStatusToast/EmpExportInfoModal.vue";
import { LogService } from "@/_services/log.Service";
import { userService } from "@/_services/user.service";
import {
  oplogExportHud,
  oplogExportHudStartJob,
  oplogExportHudEndPreparing,
  oplogExportHudHideToast,
  oplogExportHudFinishJob,
} from "@/_helpers/oplogExportHud.store";
import { empDownloadHudOpen, empDownloadHudClose } from "@/_helpers/empDownloadHud.store";

/** Excel「表单数据」列写入前的最大字符数（摘要，避免单格过大） */
const EXCEL_FORMDATA_PREVIEW_MAX = 800;
/** 列表/摘要列内 JSON 预览最大字符 */
const TABLE_JSON_PREVIEW_MAX = 45;
/** Excel 单单元格最大字符数（与 SheetJS / Excel 一致），导出时超长截断 */
const EXCEL_EXPORT_CELL_CHAR_MAX = 32767;
/** 导出审计 JSON：UI/API 筛选项为「全部」时的统一占位（便于报表阅读，非 API 传参） */
const OPLOG_EXPORT_FILTER_ALL = "All";

function isOpLogExportFilterAll(raw) {
  if (raw == null) return true;
  const s = String(raw).trim();
  return s === "" || s === "0" || s.toLowerCase() === "all";
}

function normalizeOpLogMetaCodeRows(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const row of rows) {
    if (row == null) continue;
    if (typeof row === "string") {
      const code = String(row).trim();
      if (code) out.push({ code, displayLabel: code });
      continue;
    }
    const code = String(
      row.code ?? row.Code ?? row.formType ?? row.FormType ?? row.action ?? row.Action ?? ""
    ).trim();
    if (!code) continue;
    out.push({
      code,
      displayLabel: String(row.displayLabel ?? row.DisplayLabel ?? "").trim() || code,
    });
  }
  return out;
}

/** GetUserMyUserForOperation* 返回体中取 MyUserList（兼容字段名） */
function pickMyUserListFromPayload(payload) {
  if (!payload || typeof payload !== "object") return [];
  const raw =
    payload.MyUserList
    ?? payload.myUserList
    ?? payload.MyUsers
    ?? payload.myUsers
    ?? [];
  const list = Array.isArray(raw) ? raw : [];
  return list.map((row) => normalizeOpLogUserRow(row)).filter(Boolean);
}

/** 解析 IsActive（BIT/布尔/0/1/字符串） */
function parseOpLogActiveFlag(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  const s = String(v).trim().toLowerCase();
  if (s === "1" || s === "true" || s === "y" || s === "yes") return true;
  if (s === "0" || s === "false" || s === "n" || s === "no") return false;
  return null;
}

/** 去掉 OpLogDisplayName 末尾的 (RootLogin)/(Email) 等后缀，保留展示名 */
function stripOpLogDisplaySuffix(name) {
  const s = String(name ?? "").trim();
  if (!s) return "";
  return s
    .replace(/\s*\((RootLogin|Email|Phone|Google|LinkedIn|Wechat)\)\s*$/i, "")
    .trim();
}

/** 规范操作日志用户行（含 MappingIsActive / UserIsActive） */
function normalizeOpLogUserRow(u) {
  if (!u || typeof u !== "object") return null;
  const mappingIsActive = parseOpLogActiveFlag(
    u.mappingIsActive ?? u.MappingIsActive
  );
  const userIsActive = parseOpLogActiveFlag(u.userIsActive ?? u.UserIsActive);
  return {
    ...u,
    accountUserId: u.accountUserId ?? u.AccountUserId,
    userRole: u.userRole ?? u.UserRole,
    mappingIsActive: mappingIsActive ?? true,
    MappingIsActive: mappingIsActive ?? true,
    userIsActive: userIsActive ?? true,
    UserIsActive: userIsActive ?? true,
    rootLoginValue: u.rootLoginValue ?? u.RootLoginValue,
    RootLoginValue: u.rootLoginValue ?? u.RootLoginValue,
    peopleValue: u.peopleValue ?? u.PeopleValue,
    PeopleValue: u.peopleValue ?? u.PeopleValue,
    opLogDisplayName: u.opLogDisplayName ?? u.OpLogDisplayName,
    OpLogDisplayName: u.opLogDisplayName ?? u.OpLogDisplayName,
  };
}

function metaCodeRowsToSelectOptions(rows, humanize) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  for (const r of rows) {
    if (!r) continue;
    const c = String(r.code || "").trim();
    if (!c) continue;
    const textRaw = String(r.displayLabel || "").trim();
    out.push({ value: c, text: textRaw || humanize(c) || c });
  }
  return out;
}

export default {
  name: "SupplierOperationLog",
  components: { EmpExportInfoModal },
  props: {
    supplierId: [String, Number],
    simulatedSupplier: Object,
  },
  data() {
    return {
      loading: false,
      downloading: false,
      /** 重設篩選與重載元數據／列表期間：按鈕轉圈、遮罩與禁用並行操作 */
      resettingFilters: false,
      /** 首屏請求期間：校驗總筆數是否超過上限（按鈕顯示「等待中」） */
      exportCheckingLimit: false,
      exportLimitModalVisible: false,
      /** 列表/导出首包 maxExportRows（proc DECLARE） */
      opLogMaxExportRows: null,
      exportFailureModalVisible: false,
      exportQueueModalVisible: false,
      hint: "",
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      autoRefreshTimer: null,
      refreshSeq: 0,
      selected: null,
      /** 打开详情弹窗时的日志类型（与当前 mode 同步） */
      detailMode: "FORM",
      detailModalVisible: false,
      mode: "FORM",
      filters: {
        companyId: "",
        actionByUser: "",
        action: "",
        formType: "",
        fromDate: "",
        toDate: "",
      },
      // meta caches
      metaFormTypes: [],
      metaFormActions: [],
      metaUserActions: [],
      metaFormOperators: [],
      metaUserActionOperators: [],
      /** log_action.LogActionType 下拉（用户动作/登录模式） */
      metaUserActionLogTypes: [],
      // users cache
      myUsers: [],
      /** 日志里出现但不在 MyUserList 中的操作人，通过 GetUserDetail 补齐姓名 */
      extraUserProfiles: [],
      /** 防止 mounted + 父级 Tab 激活 短时间内重复 bootstrap */
      _oplogBootstrapAt: 0,
    };
  },
  computed: {
    companyId() {
      const fromProp = this.supplierId;
      if (fromProp !== null && fromProp !== undefined && String(fromProp) !== "") return fromProp;
      const u = this.simulatedSupplier || this.$store?.state?.authentication?.user || {};
      return u.empCompanyId || u.EmpCompanyId || "";
    },
    companyDisplayName() {
      const langId = this.getCurrentLangId();
      const s = this.simulatedSupplier || {};
      const sc = s.companyNameSc || s.CompanyNameSc || s.companyNameCN || s.CompanyNameCN || s.companyName || s.CompanyName || "";
      const tc = s.companyNameTc || s.CompanyNameTc || s.companyNameTC || s.CompanyNameTC || sc;
      const en = s.companyNameEn || s.CompanyNameEn || "";
      if (langId === 950) return tc || sc || en || "";
      if (langId === 1252) return en || sc || tc || "";
      return sc || tc || en || "";
    },
    /** 與頁頭一致：公司名 (EmpCompanyId)，供全局導出 Toast 標題行展示 */
    exportToastCompanyLine() {
      const name = String(this.companyDisplayName || "").trim();
      const id =
        this.companyId != null && String(this.companyId) !== ""
          ? String(this.companyId)
          : "";
      if (name && id) return `${name} (${id})`;
      if (name) return name;
      if (id) return `(${id})`;
      return "";
    },
    modeOptions() {
      return [
        { value: "FORM", text: String(this.$t("lbl_EMPBE_OpLog_LogType_Form") || "") },
        { value: "USER_ACTION", text: String(this.$t("lbl_EMPBE_OpLog_LogType_UserAction") || "") },
      ];
    },
    userMap() {
      const m = new Map();
      const idSet = new Set();
      (this.myUsers || []).forEach((u) => {
        const id = u?.accountUserId ?? u?.AccountUserId;
        if (id != null && id !== "") idSet.add(String(id));
      });
      (this.extraUserProfiles || []).forEach((u) => {
        const id = u?.accountUserId ?? u?.AccountUserId;
        if (id != null && id !== "") idSet.add(String(id));
      });
      [
        ...(this.metaFormOperators || []),
        ...(this.metaUserActionOperators || []),
      ]
        .map((x) => String(x))
        .filter((x) => x && x !== "0")
        .forEach((x) => idSet.add(x));
      (this.items || []).forEach((row) => {
        const uid = row?.actionByUser ?? row?.ActionByUser;
        if (uid != null && uid !== "" && uid !== 0) idSet.add(String(uid));
      });
      // 子账号删除后主数据可能无姓名/邮箱可读，但日志中 ActionByUser 仍保留——必须始终以 ID 为稽核兜底
      idSet.forEach((idStr) => {
        const merged = this.mergeProfileForOperatorId(idStr);
        const label = merged ? this.formatOperatorDisplay(merged).text : "";
        m.set(idStr, label || `ID ${idStr}`);
      });
      return m;
    },
    /** 表格用户列 HTML（根账号 / 已移除 标红） */
    operatorHtmlMap() {
      const m = new Map();
      const collect = (u) => {
        const id = u?.accountUserId ?? u?.AccountUserId;
        if (id == null || id === "") return;
        const idStr = String(id);
        if (m.has(idStr)) return;
        const disp = this.formatOperatorDisplay(u);
        if (disp.html) m.set(idStr, disp.html);
      };
      (this.myUsers || []).forEach(collect);
      (this.extraUserProfiles || []).forEach(collect);
      [
        ...(this.metaFormOperators || []),
        ...(this.metaUserActionOperators || []),
      ]
        .map((x) => String(x))
        .filter((x) => x && x !== "0")
        .forEach((idStr) => {
          if (m.has(idStr)) return;
          const merged = this.mergeProfileForOperatorId(idStr);
          if (merged) {
            const disp = this.formatOperatorDisplay(merged);
            if (disp.html) m.set(idStr, disp.html);
          }
        });
      return m;
    },
    userOptions() {
      const base = [{ value: "", text: String(this.$t("lbl_EMPBE_OpLog_AllUsers") || "") }];
      const ids = new Set();

      // 1) 公司名下所有用户（用于分析时完整选择范围）
      (this.myUsers || []).forEach((u) => {
        const id = u?.accountUserId ?? u?.AccountUserId;
        if (!id) return;
        ids.add(String(id));
      });

      // 2) 日志 META 里出现过的操作人
      (this.mode === "FORM" ? this.metaFormOperators : this.metaUserActionOperators)
        .map((x) => String(x))
        .filter((x) => x && x !== "0")
        .forEach((x) => ids.add(x));
      // 3) 当前列表页（META 可能尚未刷新时兜底）
      (this.items || []).forEach((row) => {
        const uid = row?.actionByUser ?? row?.ActionByUser;
        if (uid != null && uid !== "" && uid !== 0) ids.add(String(uid));
      });

      const seenText = new Set();
      const opts = Array.from(ids)
        .map((id) => {
          const full = this.userMap.get(id) || `ID ${id}`;
          let text = String(this.truncateText(full, 28) || "");
          if (seenText.has(text)) {
            text = `${text} #${id}`;
          } else {
            seenText.add(text);
          }
          return { value: String(id), text };
        })
        .sort((a, b) => a.text.localeCompare(b.text));
      return base.concat(opts);
    },
    /** 用户下拉按钮展示（含彩色角色/停用标签） */
    userFilterButtonHtml() {
      const allLabel = String(this.$t("lbl_EMPBE_OpLog_AllUsers") || "");
      const raw = this.filters.actionByUser;
      if (raw == null || raw === "" || String(raw).trim() === "") {
        return `<span class="oplog__user-filter-plain">${this.escapeOpLogHtml(allLabel)}</span>`;
      }
      return this.operatorFilterHtml(String(raw));
    },
    /** 用户下拉菜单项（HTML，与表格列配色一致，超长省略） */
    userFilterMenuOptions() {
      return (this.userOptions || [])
        .filter((o) => o && o.value !== "")
        .map((o) => ({
          value: o.value,
          html: this.operatorFilterHtml(String(o.value)),
        }));
    },
    actionOptions() {
      const base = [{ value: "", text: String(this.$t("lbl_EMPBE_OpLog_AllActions") || "") }];
      const rows = this.mode === "FORM" ? this.metaFormActions : this.metaUserActions;
      return base.concat(this.opLogMetaSelectOptions(rows));
    },
    formTypeOptions() {
      const base = [{ value: "", text: String(this.$t("lbl_EMPBE_OpLog_AllTypes") || "") }];
      return base.concat(this.opLogMetaSelectOptions(this.metaFormTypes));
    },
    userActionLogTypeOptions() {
      const base = [{ value: "", text: String(this.$t("lbl_EMPBE_OpLog_AllLogActionTypes") || "") }];
      return base.concat(this.opLogMetaSelectOptions(this.metaUserActionLogTypes));
    },
    typeFilterLabel() {
      return this.mode === "FORM"
        ? String(this.$t("lbl_EMPBE_OpLog_FormType") || "")
        : String(this.$t("lbl_EMPBE_OpLog_LogActionType") || "");
    },
    typeFilterOptions() {
      return this.mode === "FORM" ? this.formTypeOptions : this.userActionLogTypeOptions;
    },
    exportLimitModalSubtitle() {
      const raw = this.opLogMaxExportRows;
      const n = raw != null && Number.isFinite(Number(raw)) ? Math.floor(Number(raw)) : null;
      if (n != null && n > 0) {
        const loc = this.getOpLogNumberLocale();
        return String(this.$t("lbl_EMPBE_OpLog_ExportLimitModalSubtitle", { n: n.toLocaleString(loc) }));
      }
      return String(this.$t("lbl_EMPBE_OpLog_ExportLimitModalSubtitleUnknown"));
    },
    canQuery() {
      const companyId = this.filters.companyId || this.companyId;
      return !!(companyId && String(companyId) !== "0");
    },
    /** 其它頁面/實例正在導出，本頁不可再點 */
    oplogExportLockedElsewhere() {
      return oplogExportHud.active && !this.downloading;
    },
    /** 导出按钮：idle=outline primary；等待上限=checking；导出中=busy（失敗改居中彈窗，不再用右下角紅 Toast） */
    exportActionButtonClass() {
      if (this.oplogExportLockedElsewhere) return "btn-outline-primary emp-dl-export-btn--remote-busy";
      if (this.downloading && this.exportCheckingLimit) return "emp-dl-export-btn--checking";
      if (this.downloading) return "emp-dl-export-btn--busy";
      return "btn-outline-primary";
    },
    exportButtonLabel() {
      if (this.oplogExportLockedElsewhere) return this.$t("lbl_EMPBE_OpLog_ExportBlockedLabel");
      if (!this.downloading) return this.$t("lbl_EMPBE_OpLog_ExportData");
      if (this.exportCheckingLimit) return this.$t("lbl_EMPBE_OpLog_ExportWait");
      return this.$t("lbl_EMPBE_OpLog_Exporting");
    },
    exportButtonTitleAttr() {
      if (this.oplogExportLockedElsewhere) return this.$t("lbl_EMPBE_OpLog_ExportGlobalBusyHint");
      if (this.downloading && this.exportCheckingLimit) return this.$t("lbl_EMPBE_OpLog_ExportWait");
      if (this.downloading) return this.$t("lbl_EMPBE_OpLog_Exporting");
      return "";
    },
    tableFields() {
      if (this.mode === "USER_ACTION") {
        return [
          {
            key: "actionByUser",
            label: this.$t("lbl_EMPBE_OpLog_User"),
            thClass: "oplog__col-tight",
            tdClass: "oplog__col-tight",
          },
          {
            key: "createdDate",
            label: this.$t("lbl_EMPBE_OpLog_CreatedDate"),
            thClass: "oplog__col-tight",
            tdClass: "oplog__col-tight",
          },
          {
            key: "action",
            label: this.$t("lbl_EMPBE_OpLog_Action"),
            thClass: "oplog__col-tight",
            tdClass: "oplog__col-tight",
          },
          {
            key: "formType",
            label: this.$t("lbl_EMPBE_OpLog_LogActionType"),
            thClass: "oplog__col-tight",
            tdClass: "oplog__col-tight",
          },
          {
            key: "detailPreview",
            label: this.$t("lbl_EMPBE_OpLog_DetailPreview"),
            thClass: "oplog__col-formdata",
            tdClass: "oplog__col-formdata",
          },
        ];
      }
      return [
        {
          key: "actionByUser",
          label: this.$t("lbl_EMPBE_OpLog_User"),
          thClass: "oplog__col-tight",
          tdClass: "oplog__col-tight",
        },
        {
          key: "createdDate",
          label: this.$t("lbl_EMPBE_OpLog_CreatedDate"),
          thClass: "oplog__col-tight",
          tdClass: "oplog__col-tight",
        },
        {
          key: "action",
          label: this.$t("lbl_EMPBE_OpLog_Action"),
          thClass: "oplog__col-tight",
          tdClass: "oplog__col-tight",
        },
        {
          key: "formType",
          label: this.$t("lbl_EMPBE_OpLog_FormType"),
          thClass: "oplog__col-tight",
          tdClass: "oplog__col-tight",
        },
        {
          key: "formData",
          label: this.$t("lbl_EMPBE_OpLog_FormDataPreview"),
          thClass: "oplog__col-formdata",
          tdClass: "oplog__col-formdata",
        },
      ];
    },
    emptyText() {
      return this.hint ? this.hint : this.$t("lbl_EMPBE_OpLog_Empty");
    },
  },
  watch: {
    companyId() {
      // 切换公司且组件已挂载时：重新按默认日期范围拉 META + 列表
      this.performBootstrap({ resetDates: true, loadList: true });
    },
  },
  mounted() {
    // 点击「操作日志」Tab 后 v-if 才挂载本组件，此处再拉一次，避免仅依赖 watch 时序
    this.$nextTick(() => {
      this.$nextTick(() => {
        this.performBootstrap({ resetDates: true, loadList: true });
      });
    });
  },
  methods: {
    /**
     * 开发调试：仅写控制台，不改变任何业务数据与分支。
     * 默认输出；在浏览器执行 window.__EMP_OPLOG_DEBUG__ = false 可关闭本页与 LogService 中 [OpLog] 类调试输出。
     */
    __opLogDevLog(...args) {
      try {
        if (typeof window !== "undefined" && window.__EMP_OPLOG_DEBUG__ === false) return;
        if (typeof console !== "undefined" && typeof console.error === "function") {
          console.error(...args);
        }
      } catch (_) {
        /* ignore */
      }
    },
    /**
     * 由父级 default.vue 在切换到操作日志 Tab 时调用（与 mounted 二选一或短时间去重）
     */
    bootstrapFromTab() {
      this.performBootstrap({ resetDates: true, loadList: true });
    },
    async performBootstrap({ resetDates, loadList }) {
      if (!this.companyId || String(this.companyId) === "0") return;
      const now = Date.now();
      if (this._oplogBootstrapAt && now - this._oplogBootstrapAt < 450) return;
      this._oplogBootstrapAt = now;

      this.extraUserProfiles = [];
      this.page = 1;
      this.items = [];
      this.total = 0;
      this.hint = "";
      this.opLogMaxExportRows = null;
      this.filters.companyId = this.companyId ? String(this.companyId) : "";

      const today = new Date();
      const to = today.toISOString().slice(0, 10);
      const from = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      if (resetDates) {
        this.filters.fromDate = from;
        this.filters.toDate = to;
      } else {
        if (!this.filters.fromDate) this.filters.fromDate = from;
        if (!this.filters.toDate) this.filters.toDate = to;
      }

      await this.loadMeta();
      await this.loadUserOptions();
      await this.enrichMissingOperatorProfiles();
      if (loadList) this.scheduleRefresh({ immediate: true });
    },
    runSearch() {
      this.page = 1;
      this.scheduleRefresh({ immediate: true });
    },
    async onLogTypeChange() {
      this.filters.formType = "";
      this.page = 1;
      this.items = [];
      this.total = 0;
      this.hint = this.$t("lbl_EMPBE_OpLog_ClickSearchHint");
      this.opLogMaxExportRows = null;
      await this.loadMeta();
      await this.loadUserOptions();
      await this.enrichMissingOperatorProfiles();
    },
    onPageChange(newPage) {
      // BootstrapVue 的 @change 可能早于 v-model 完成更新；使用事件参数保证页码一致
      const p = Number(newPage);
      this.page = Number.isFinite(p) && p > 0 ? p : 1;
      this.scheduleRefresh({ immediate: true });
    },
    scheduleRefresh({ immediate = false } = {}) {
      if (this.autoRefreshTimer) {
        clearTimeout(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
      const delay = immediate ? 0 : 450;
      this.autoRefreshTimer = setTimeout(() => {
        this.autoRefreshTimer = null;
        this.refresh();
      }, delay);
    },
    isUnknownActionByUser(v) {
      if (v === null || v === undefined) return true;
      if (typeof v === "number") return v <= 0;
      const s = String(v).trim();
      return !s || s === "0";
    },
    truncateText(text, max = 28) {
      const s = text == null ? "" : String(text);
      if (!s) return "";
      if (s.length <= max) return s;
      return s.slice(0, Math.max(0, max - 1)) + "…";
    },
    parseDateMs(v) {
      if (!v) return 0;
      const s = String(v);
      const ms = Date.parse(s);
      if (Number.isFinite(ms)) return ms;
      // 兼容 "YYYY-MM-DD HH:mm:ss"（无时区）格式
      const ms2 = Date.parse(s.replace(" ", "T"));
      return Number.isFinite(ms2) ? ms2 : 0;
    },
    getCurrentLangId() {
      const n = parseInt(localStorage.getItem("currentLangId"));
      return Number.isFinite(n) ? n : 936;
    },
    /** toLocaleString：与 currentLangId 对齐 */
    getOpLogNumberLocale() {
      const id = this.getCurrentLangId();
      if (id === 950) return "zh-HK";
      if (id === 1252) return "en";
      return "zh-CN";
    },
    getCurrentAccountUserId() {
      const u = this.$store?.state?.authentication?.user || {};
      return (
        u.accountUserId
        ?? u.AccountUserId
        ?? u.accountUserID
        ?? u.AccountUserID
        ?? null
      );
    },
    opLogApiOk(res) {
      if (!res || typeof res !== "object") return false;
      return res.isSuccessful === true || res.IsSuccessful === true;
    },
    /** 用户动作：原始 Action → 与后端一致的动作族键 */
    normalizeUserActionFilterKey(action) {
      const t = String(action == null ? "" : action).trim();
      if (!t) return "";
      if (/^Check LoginName:/i.test(t)) return "Check LoginName";
      if (/^Account Login:/i.test(t)) return "Account Login";
      if (/^Account NewLogin:/i.test(t)) return "Account NewLogin";
      if (/^Mark EMAIL IsVerify/i.test(t)) return "Mark EMAIL IsVerify";
      if (/^Mark PHONE IsVerify/i.test(t)) return "Mark PHONE IsVerify";
      if (/^Update Password for AccountUserId:/i.test(t)) return "Update Password";
      if (/^Failed to Create New Root Login/i.test(t)) return "Failed to Create New Root Login";
      if (/^Create New Root Login/i.test(t)) return "Create New Root Login";
      if (/^Send Email/i.test(t)) return "Send Email";
      if (/^Send Forget Password/i.test(t)) return "Send Forget Password";
      if (/^Retrieve password via Email/i.test(t)) return "Retrieve password via Email";
      if (/^Retrieve password via Phone/i.test(t)) return "Retrieve password via Phone";
      if (/^Retrieve username via Email/i.test(t)) return "Retrieve username via Email";
      if (/^Retrieve username via Phone/i.test(t)) return "Retrieve username via Phone";
      if (/^Retrieve Credential Type:/i.test(t)) return "Retrieve Credential";
      if (/^Request Verification Code via Email/i.test(t)) return "Request Verification Code via Email";
      if (/^GetInternalUserToken/i.test(t)) return "GetInternalUserToken";
      if (/^\[TEST] Lang override/i.test(t) || /Lang override check;/i.test(t)) return "TestLangOverride";
      if (/^Edit User Role/i.test(t)) return "Edit User Role";
      if (/^(add|Add) User Role/i.test(t)) return "Add User Role";
      if (/^(Del|Delete) User Role/i.test(t)) return "Del User Role";
      return t;
    },
    syncUserActionFilterValueWithMeta() {
      if (this.mode !== "USER_ACTION") return;
      const a = this.filters.action;
      if (a === null || a === undefined || String(a).trim() === "") return;
      this.filters.action = this.normalizeUserActionFilterKey(String(a));
    },
    humanizeOpLogCodeKey(code) {
      let s = String(code || "").trim();
      if (!s) return "";
      s = s.replace(/_/g, " ");
      s = s.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
      s = s.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
      return s.trim();
    },
    opLogMetaSelectOptions(rows) {
      return metaCodeRowsToSelectOptions(rows, (c) => this.humanizeOpLogCodeKey(c));
    },
    actionDisplayLabelForMode(action, mode) {
      const v = String(action == null ? "" : action).trim();
      if (!v) return "";
      if (mode === "USER_ACTION") {
        const canon = this.normalizeUserActionFilterKey(v);
        return this.humanizeOpLogCodeKey(canon || v);
      }
      return this.humanizeOpLogCodeKey(v);
    },
    columnActionDisplayLabel(item, mode) {
      const db = item?.displayAction ?? item?.DisplayAction;
      if (db != null && String(db).trim() !== "") return String(db).trim();
      return this.actionDisplayLabelForMode(item?.action, mode);
    },
    columnFormTypeDisplayLabel(item) {
      const db = item?.displayFormType ?? item?.DisplayFormType;
      if (db != null && String(db).trim() !== "") return String(db).trim();
      const raw = item?.formType;
      if (raw == null || String(raw).trim() === "") return "";
      const s = String(raw).trim();
      return this.humanizeOpLogCodeKey(s);
    },
    actionColumnDisplayLabel(item) {
      return this.columnActionDisplayLabel(item, this.mode);
    },
    formTypeColumnDisplayLabel(item) {
      return this.columnFormTypeDisplayLabel(item);
    },
    detailFormTypeDisplayLabel() {
      const selected = this.selected;
      if (!selected) return "";
      return this.columnFormTypeDisplayLabel(selected);
    },
    detailActionDisplayLabel() {
      const selected = this.selected;
      if (!selected) return "";
      return this.columnActionDisplayLabel(selected, this.detailMode);
    },
    getOpLogCompanyIdStr() {
      const cid = this.filters.companyId || this.companyId;
      return cid != null && String(cid).trim() !== "" ? String(cid).trim() : "";
    },
    escapeOpLogHtml(s) {
      return String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    /** 平台代操作人：AccountType=EXH/ADM，或有 PEOPLE 且 RootLogin 非本公司 CP */
    isExhOperator(x) {
      if (!x) return false;
      if (x.exhOperator === true || x.ExhOperator === true) return true;
      const acct = String(x?.accountType ?? x?.AccountType ?? "").trim().toUpperCase();
      if (acct === "EXH" || acct === "ADM") return true;
      const companyId = this.getOpLogCompanyIdStr();
      const companyCp = companyId ? `CP${companyId}`.toUpperCase() : "";
      const people = String(x.peopleValue ?? x.PeopleValue ?? "").trim();
      const root = String(x.rootLoginValue ?? x.RootLoginValue ?? "").trim().toUpperCase();
      return !!(people && root && /^CP\d+$/i.test(root) && root !== companyCp);
    },
    isOperatorInactive(x) {
      if (!x) return false;
      const mi = parseOpLogActiveFlag(x.mappingIsActive ?? x.MappingIsActive);
      const ua = parseOpLogActiveFlag(x.userIsActive ?? x.UserIsActive);
      return mi === false || ua === false;
    },
    /** 当前公司 ROOT：RootLogin=CP{公司Id}（非 EXH 平台账号） */
    isCompanyRootOperator(x) {
      if (!x || this.isExhOperator(x)) return false;
      const companyId = this.getOpLogCompanyIdStr();
      if (!companyId) return false;
      const root = String(x.rootLoginValue ?? x.RootLoginValue ?? "").trim().toUpperCase();
      return root === `CP${companyId}`.toUpperCase();
    },
    opLogOperatorPersonName(x) {
      if (!x) return "";
      const langId = this.getCurrentLangId();
      const sc = x.personNameSc ?? x.PersonNameSc;
      const tc = x.personNameTC ?? x.PersonNameTC ?? x.personNameTc ?? x.PersonNameTc;
      const en = x.personNameEn ?? x.PersonNameEn;
      if (langId === 950) return String(tc || sc || en || "").trim();
      if (langId === 1252) return String(en || sc || tc || "").trim();
      return String(sc || tc || en || "").trim();
    },
    opLogOperatorAccountName(x) {
      if (!x) return "";
      const people = String(x.peopleValue ?? x.PeopleValue ?? "").trim();
      const rootLogin = String(x.rootLoginValue ?? x.RootLoginValue ?? "").trim();
      if (this.isExhOperator(x)) {
        if (people) return people;
        if (rootLogin) return rootLogin;
      }
      const pn = this.opLogOperatorPersonName(x);
      if (pn) return pn;
      const fromOpLog = stripOpLogDisplaySuffix(
        x.opLogDisplayName ?? x.OpLogDisplayName
      );
      if (fromOpLog) return fromOpLog;
      if (rootLogin) return rootLogin;
      const email = x.emailValue ?? x.EmailValue ?? x.loginEmail ?? x.LoginEmail;
      if (email && String(email).trim()) return String(email).trim();
      const pv = x.phoneValue ?? x.PhoneValue;
      if (pv && String(pv).trim()) return String(pv).trim();
      const dn = x.displayName ?? x.DisplayName;
      if (dn && String(dn).trim()) return String(dn).trim();
      return "";
    },
    /** 仅根账号、后台管理员展示角色；普通管理员/成员不展示 */
    opLogOperatorRoleInfo(x) {
      if (!x) return { label: "", tone: "default" };
      if (this.isExhOperator(x)) {
        return {
          label: String(this.$t("lbl_EMPBE_OpLog_UserRoleBackendAdmin") || "").trim(),
          tone: "backend",
        };
      }
      if (this.isCompanyRootOperator(x)) {
        return {
          label: String(this.$t("lbl_EMPBE_OpLog_UserRoleRootLabel") || "").trim(),
          tone: "root",
        };
      }
      return { label: "", tone: "default" };
    },
    /** 用户下拉等窄控件：账号名截断，保留彩色角色/停用后缀 */
    operatorFilterHtml(idStr, maxAccountLength = 22) {
      const merged = this.mergeProfileForOperatorId(String(idStr));
      if (merged) {
        return this.formatOperatorDisplay(merged, { maxAccountLength }).html;
      }
      const plain = this.userMap.get(String(idStr)) || `ID ${idStr}`;
      return this.escapeOpLogHtml(this.truncateOpLogPlainText(plain, maxAccountLength + 12));
    },
    truncateOpLogPlainText(text, maxLen) {
      const s = String(text ?? "");
      if (!maxLen || s.length <= maxLen) return s;
      return `${s.slice(0, Math.max(1, maxLen - 1))}…`;
    },
    /** 账号（仅根账号/后台管理员带角色）+ 已停用标红 */
    formatOperatorDisplay(x, options) {
      if (!x) return { text: "", html: "" };
      const maxAccountLength =
        options && options.maxAccountLength != null
          ? Number(options.maxAccountLength)
          : 0;
      const account = this.opLogOperatorAccountName(x);
      const roleInfo = this.opLogOperatorRoleInfo(x);
      const inactive = this.isOperatorInactive(x);
      const inactiveText = inactive
        ? String(this.$t("lbl_EMPBE_OpLog_UserInactiveSuffix") || "").trim()
        : "";
      const uid = x.accountUserId ?? x.AccountUserId;
      let base =
        account
        || (uid != null && uid !== "" ? `ID ${uid}` : "");
      if (!base) return { text: "", html: "" };
      if (maxAccountLength > 0 && base.length > maxAccountLength) {
        base = this.truncateOpLogPlainText(base, maxAccountLength);
      }
      const roleLabel = roleInfo.label || "";
      const roleParen = roleLabel ? `（${roleLabel}）` : "";
      const text = `${base}${roleParen}${inactiveText}`;
      let html = this.escapeOpLogHtml(base);
      if (roleLabel) {
        let cls = "oplog__op-tag";
        if (roleInfo.tone === "root") cls = "oplog__op-tag oplog__op-tag--root";
        else if (roleInfo.tone === "backend") cls = "oplog__op-tag oplog__op-tag--backend";
        html += `<span class="${cls}">（${this.escapeOpLogHtml(roleLabel)}）</span>`;
      }
      if (inactiveText) {
        html += `<span class="oplog__op-tag oplog__op-tag--inactive">${this.escapeOpLogHtml(inactiveText)}</span>`;
      }
      return { text, html };
    },
    inferOperatorLabel(x) {
      return this.formatOperatorDisplay(x).text;
    },
    /** 用户筛选项 → API 的 ActionByUser（仅有效正整数） */
    parseActionByUserFilter(raw) {
      if (raw == null || raw === "") return null;
      const n = Number.parseInt(String(raw), 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    mergeProfileForOperatorId(idStr) {
      const fromMy = (this.myUsers || []).find(
        (u) => String(u?.accountUserId ?? u?.AccountUserId) === idStr
      );
      const fromEx = (this.extraUserProfiles || []).find(
        (u) => String(u?.accountUserId ?? u?.AccountUserId) === idStr
      );
      if (!fromMy && !fromEx) return null;
      const merged = normalizeOpLogUserRow({ ...fromEx, ...fromMy }) || { ...fromEx, ...fromMy };
      if (fromMy) {
        merged.accountType = undefined;
        merged.AccountType = undefined;
        merged.exhOperator = false;
        merged.ExhOperator = false;
        const myNorm = normalizeOpLogUserRow(fromMy);
        if (myNorm) {
          merged.mappingIsActive = myNorm.mappingIsActive;
          merged.MappingIsActive = myNorm.MappingIsActive;
          merged.userIsActive = myNorm.userIsActive;
          merged.UserIsActive = myNorm.UserIsActive;
        }
      }
      if (fromEx && (fromEx.exhOperator === true || fromEx.ExhOperator === true)) {
        merged.exhOperator = true;
        merged.accountType = fromEx.accountType ?? fromEx.AccountType;
        merged.peopleValue = fromEx.peopleValue ?? fromEx.PeopleValue ?? merged.peopleValue;
        merged.PeopleValue = merged.peopleValue;
        merged.rootLoginValue =
          fromEx.rootLoginValue ?? fromEx.RootLoginValue ?? merged.rootLoginValue;
        merged.RootLoginValue = merged.rootLoginValue;
      }
      return merged;
    },
    /** 将 GetUserDetail / GetOpLogOperatorProfile 返回体规范为操作人展示结构 */
    mapOpLogOperatorProfile(d, accountUserId) {
      if (!d || typeof d !== "object") return null;
      const accountType = d.accountType ?? d.AccountType;
      const acctUpper = String(accountType ?? "").trim().toUpperCase();
      const isPlatform = acctUpper === "EXH" || acctUpper === "ADM";
      const people = String(d.peopleValue ?? d.PeopleValue ?? "").trim();
      const root = String(d.rootLoginValue ?? d.RootLoginValue ?? "").trim().toUpperCase();
      const isPlatformByLogin = !!(people && root && /^CP\d+$/i.test(root));
      const mapActive = parseOpLogActiveFlag(d.mappingIsActive ?? d.MappingIsActive);
      const usrActive = parseOpLogActiveFlag(d.userIsActive ?? d.UserIsActive);
      return {
        accountUserId: Number(accountUserId) || d.accountUserId || d.AccountUserId,
        userRole: d.userRole ?? d.UserRole,
        accountType: isPlatform ? accountType : undefined,
        exhOperator: isPlatform || isPlatformByLogin,
        mappingIsActive: mapActive ?? true,
        MappingIsActive: mapActive ?? true,
        userIsActive: usrActive ?? true,
        UserIsActive: usrActive ?? true,
        opLogDisplayName: d.opLogDisplayName ?? d.OpLogDisplayName,
        personNameSc: d.personNameSc ?? d.PersonNameSc,
        personNameTC: d.personNameTC ?? d.PersonNameTC ?? d.personNameTc ?? d.PersonNameTc,
        personNameEn: d.personNameEn ?? d.PersonNameEn,
        displayName: d.displayName ?? d.DisplayName,
        emailValue: d.emailValue ?? d.EmailValue ?? d.loginEmail ?? d.LoginEmail,
        phoneValue: d.phoneValue ?? d.PhoneValue,
        phoneCountry: d.phoneCountry ?? d.PhoneCountry,
        rootLoginValue: d.rootLoginValue ?? d.RootLoginValue,
        peopleValue: d.peopleValue ?? d.PeopleValue,
        googleValue: d.googleValue ?? d.GoogleValue,
        linkedinValue: d.linkedinValue ?? d.LinkedInValue ?? d.linkedInValue,
        wechatValue: d.wechatValue ?? d.WechatValue,
        loginEmail: d.loginEmail ?? d.LoginEmail,
      };
    },
    needsOperatorProfileEnrich(idStr) {
      const merged = this.mergeProfileForOperatorId(idStr);
      if (!merged) return true;
      const label = this.formatOperatorDisplay(merged).text;
      if (!label || /^ID\s+\d+$/i.test(String(label).trim())) return true;
      const companyCp = `CP${this.getOpLogCompanyIdStr()}`.toUpperCase();
      const root = String(merged.rootLoginValue ?? merged.RootLoginValue ?? "").trim().toUpperCase();
      const people = String(merged.peopleValue ?? merged.PeopleValue ?? "").trim();
      if (root === companyCp && !label.includes("ROOT")) return true;
      if (root && /^CP\d+$/i.test(root) && root !== companyCp && !people && !this.isExhOperator(merged)) {
        return true;
      }
      return false;
    },
    /** 表格“用户”列：与 userMap 联动，补全姓名后自动更新 */
    operatorFullLabel(item) {
      const uid = item?.actionByUser;
      if (uid === null || uid === undefined || uid === "") return "-";
      return this.userMap.get(String(uid)) || `ID ${uid}`;
    },
    operatorCellHtml(item) {
      const uid = item?.actionByUser;
      if (uid === null || uid === undefined || uid === "") return "-";
      const html =
        this.operatorHtmlMap.get(String(uid))
        || this.escapeOpLogHtml(`ID ${uid}`);
      return html;
    },
    /** 悬停时始终带出 AccountUserId，便于审计（含子账号删除后仅存日志 ID 的场景） */
    operatorAuditTooltip(item) {
      const uid = item?.actionByUser;
      if (uid === null || uid === undefined || uid === "") return "";
      const primary = this.operatorFullLabel(item);
      const auditLine = this.$t("lbl_EMPBE_OpLog_AuditAccountUserIdLine", {
        id: String(uid),
      });
      return primary === "-" ? auditLine : `${primary}\n${auditLine}`;
    },
    async loadMeta() {
      try {
        const companyId = this.filters.companyId || this.companyId || "";
        const req = {
          companyId: companyId ? Number(companyId) || companyId : 0,
          uiLangId: this.getCurrentLangId(),
        };
        const res = await LogService.GetLogFormMeta(req);
        if (!res || !this.opLogApiOk(res)) return;
        const d = res?.data ?? {};
        this.metaFormTypes = normalizeOpLogMetaCodeRows(d.formTypes);
        this.metaFormActions = normalizeOpLogMetaCodeRows(d.formActions);
        this.metaUserActions = normalizeOpLogMetaCodeRows(d.userActions);
        this.metaFormOperators = Array.isArray(d.formOperators) ? d.formOperators : [];
        this.metaUserActionOperators = Array.isArray(d.userActionOperators) ? d.userActionOperators : [];
        this.metaUserActionLogTypes = normalizeOpLogMetaCodeRows(d.userActionLogTypes);
        this.syncUserActionFilterValueWithMeta();
      } catch (e) {
        this.__opLogDevLog("[OpLog page] loadMeta exception", e);
      }
    },
    async loadUserOptions() {
      const companyId = this.filters.companyId || this.companyId;
      if (!companyId) return;
      try {
        const u = this.$store?.state?.authentication?.user || {};
        const isAdmin = String(u.userType || u.UserType || "").toUpperCase() === "ADMIN";
        const currentAccountUserId = this.getCurrentAccountUserId();

        const langId = this.getCurrentLangId();
        const res = isAdmin
          ? await userService.GetUserMyUserForOperationLogByAdministrator({
              accountId: Number(companyId) || companyId,
              accountUserId: Number(currentAccountUserId) || 0,
              uiLangId: langId,
            })
          : await userService.GetUserMyUserForOperationLog({ uiLangId: langId });

        this.myUsers = pickMyUserListFromPayload(res?.data);
      } catch (e) {
        this.__opLogDevLog("[OpLog page] loadUserOptions exception", e);
        this.myUsers = [];
      }
    },
    /** META / 当前列表中的操作人：MyUserList 不足时用详情或 OpLog 专用接口补姓名 */
    async enrichMissingOperatorProfiles() {
      const companyId = this.filters.companyId || this.companyId;
      if (!companyId) return;

      const u = this.$store?.state?.authentication?.user || {};
      const isAdmin = String(u.userType || u.UserType || "").toUpperCase() === "ADMIN";
      const langId = this.getCurrentLangId();

      const opIds = new Set();
      [
        ...(this.metaFormOperators || []),
        ...(this.metaUserActionOperators || []),
      ]
        .map((x) => String(x))
        .filter((x) => x && x !== "0")
        .forEach((x) => opIds.add(x));
      (this.items || []).forEach((row) => {
        const uid = row?.actionByUser ?? row?.ActionByUser;
        if (uid != null && uid !== "" && uid !== 0) opIds.add(String(uid));
      });

      const pending = [...opIds].filter((idStr) => {
        const inMy = (this.myUsers || []).some(
          (u) => String(u?.accountUserId ?? u?.AccountUserId) === idStr
        );
        if (!inMy) return true;
        return this.needsOperatorProfileEnrich(idStr);
      });
      if (!pending.length) return;

      const added = [];
      for (const idStr of pending) {
        const accountUserId = parseInt(idStr, 10);
        if (!Number.isFinite(accountUserId)) continue;
        const inMyUsers = (this.myUsers || []).some(
          (u) => String(u?.accountUserId ?? u?.AccountUserId) === idStr
        );
        try {
          let mapped = null;
          const fetchOpProfile = async () => {
            if (!isAdmin) return null;
            const opRes = await userService.GetOpLogOperatorProfileByAdministrator({
              accountUserId,
              uiLangId: langId,
              accountId: Number(companyId) || companyId,
            });
            if (opRes?.isSuccessful && opRes.data) {
              return this.mapOpLogOperatorProfile(opRes.data, accountUserId);
            }
            return null;
          };
          if (!inMyUsers) {
            mapped = await fetchOpProfile();
          }
          if (!mapped) {
            const detailRes = isAdmin
              ? await userService.GetUserDetailByAdministrator({
                accountId: Number(companyId) || companyId,
                accountUserId,
                LangId: langId,
              })
              : await userService.GetUserDetail({
                accountUserId,
                LangId: langId,
              });
            if (detailRes?.isSuccessful && detailRes.data) {
              mapped = this.mapOpLogOperatorProfile(detailRes.data, accountUserId);
            }
          }
          const labelNow = mapped ? this.formatOperatorDisplay(mapped).text : "";
          if (
            isAdmin
            && (!labelNow || /^ID\s+\d+$/i.test(String(labelNow).trim()))
          ) {
            const fromOp = await fetchOpProfile();
            if (fromOp) {
              if (inMyUsers && mapped) {
                const myRow = (this.myUsers || []).find(
                  (u) => String(u?.accountUserId ?? u?.AccountUserId) === idStr
                );
                const myNorm = normalizeOpLogUserRow(myRow);
                mapped = {
                  ...fromOp,
                  ...mapped,
                  mappingIsActive: myNorm?.mappingIsActive ?? mapped.mappingIsActive,
                  MappingIsActive: myNorm?.MappingIsActive ?? mapped.MappingIsActive,
                  userIsActive: myNorm?.userIsActive ?? mapped.userIsActive,
                  UserIsActive: myNorm?.UserIsActive ?? mapped.UserIsActive,
                };
              } else {
                mapped = mapped ? { ...mapped, ...fromOp } : fromOp;
              }
            }
          }
          if (mapped) added.push(normalizeOpLogUserRow(mapped) || mapped);
        } catch (e) {
          this.__opLogDevLog("[OpLog page] enrichMissingOperatorProfiles failed", { accountUserId: idStr }, e);
        }
      }

      if (added.length) {
        const byId = new Map(
          (this.extraUserProfiles || []).map((p) => [
            String(p?.accountUserId ?? p?.AccountUserId),
            p,
          ])
        );
        added.forEach((p) => {
          const k = String(p.accountUserId);
          byId.set(k, { ...byId.get(k), ...p });
        });
        this.extraUserProfiles = Array.from(byId.values());
      }
    },
    async resetFilters() {
      if (this.autoRefreshTimer) {
        clearTimeout(this.autoRefreshTimer);
        this.autoRefreshTimer = null;
      }
      this.resettingFilters = true;
      try {
        this.filters.companyId = String(this.companyId || "");
        this.filters.actionByUser = "";
        this.filters.action = "";
        this.filters.formType = "";
        const today = new Date();
        this.filters.toDate = today.toISOString().slice(0, 10);
        this.filters.fromDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
        this.mode = "FORM";
        this.page = 1;
        this.items = [];
        this.total = 0;
        this.hint = "";
        this.opLogMaxExportRows = null;
        await this.loadMeta();
        await this.loadUserOptions();
        await this.enrichMissingOperatorProfiles();
        await this.refresh(1);
      } catch (e) {
        this.__opLogDevLog("[OpLog page] resetFilters failed", e);
      } finally {
        this.resettingFilters = false;
      }
    },
    userActionRowPreview(item) {
      const parts = [];
      if (item?.url) parts.push(String(item.url));
      if (item?.formType) {
        const ft = this.columnFormTypeDisplayLabel(item);
        if (ft) parts.push(ft);
      }
      if (item?.action) {
        const act = this.columnActionDisplayLabel(item, this.mode);
        if (act) parts.push(act);
      }
      const s = parts.join(" · ");
      return s ? this.previewJson(s) : "-";
    },
    /** 超长单元格截断，避免 XLSX.writeFile 抛 Text length must not exceed 32767 */
    truncateForExcelExport(val) {
      const s = val == null ? "" : String(val);
      const max = EXCEL_EXPORT_CELL_CHAR_MAX;
      if (s.length <= max) return s;
      const suffix = "…[truncated]";
      const keep = max - suffix.length;
      if (keep <= 0) return suffix.slice(0, max);
      return s.slice(0, keep) + suffix;
    },
    exportCellStr(v) {
      if (v === null || v === undefined) return "";
      return this.truncateForExcelExport(String(v));
    },
    exportFormDataCell(fd) {
      if (fd === null || fd === undefined || fd === "") return "";
      let raw;
      if (typeof fd === "string") {
        raw = fd;
      } else {
        try {
          raw = JSON.stringify(fd);
        } catch (e) {
          raw = String(fd);
        }
      }
      raw = raw.replace(/\s+/g, " ").trim();
      if (raw.length > EXCEL_FORMDATA_PREVIEW_MAX) {
        raw = `${raw.slice(0, EXCEL_FORMDATA_PREVIEW_MAX)}…`;
      }
      return this.truncateForExcelExport(raw);
    },
    formatExportTimestamp(d = new Date()) {
      const pad = (n) => String(n).padStart(2, "0");
      return (
        `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
        `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
      );
    },
    /** Excel 表头：随界面语言切换（TC/SC/EN） */
    buildExportExcelHeaders() {
      return [
        this.$t("lbl_EMPBE_OpLog_ExcelCol_Rid"),
        this.$t("lbl_EMPBE_OpLog_ExcelCol_User"),
        this.$t("lbl_EMPBE_OpLog_ExcelCol_CreatedDate"),
        this.$t("lbl_EMPBE_OpLog_ExcelCol_Action"),
        this.$t("lbl_EMPBE_OpLog_ExcelCol_FormType"),
        this.$t("lbl_EMPBE_OpLog_ExcelCol_FormId"),
        this.$t("lbl_EMPBE_OpLog_ExcelCol_CreatedBy"),
        this.$t("lbl_EMPBE_OpLog_ExcelCol_ActionByUser"),
        this.$t("lbl_EMPBE_OpLog_ExcelCol_FormData"),
      ];
    },
    /** CreatedBy 多为公司 ID：与当前页公司一致时带出公司名 */
    exportExcelCreatedByLabel(createdBy) {
      const raw = createdBy == null || createdBy === "" ? "" : String(createdBy).trim();
      if (!raw) return "";
      const cid = this.companyId != null && String(this.companyId) !== "" ? String(this.companyId) : "";
      const name = String(this.companyDisplayName || "").trim();
      if (cid && raw === cid && name) {
        return String(this.$t("lbl_EMPBE_OpLog_ExcelCreatedByCompany", { name, id: raw }));
      }
      return String(this.$t("lbl_EMPBE_OpLog_ExcelCreatedByIdOnly", { id: raw }));
    },
    exportExcelActionByLabel(actionByUser) {
      if (actionByUser === null || actionByUser === undefined || actionByUser === "") return "";
      return this.operatorFullLabel({ actionByUser });
    },
    buildExportSheet(rows, exportMode) {
      const header = this.buildExportExcelHeaders();
      const body = rows.map((item) => {
        let formDataVal = item.formData;
        if (exportMode === "USER_ACTION") {
          const extra = {
            url: item.url,
            ip: item.ip,
            bAgent: item.bAgent,
            referral: item.referral,
            domId: item.domId,
            domClass: item.domClass,
          };
          try {
            formDataVal = JSON.stringify(extra);
          } catch (e) {
            formDataVal = "";
          }
        }
        return [
          this.exportCellStr(item.rid),
          this.exportCellStr(this.operatorFullLabel(item)),
          this.exportCellStr(this.formatDate(item.createdDate)),
          this.exportCellStr(this.columnActionDisplayLabel(item, exportMode)),
          this.exportCellStr(this.columnFormTypeDisplayLabel(item)),
          this.exportCellStr(item.formId),
          this.exportCellStr(this.exportExcelCreatedByLabel(item.createdBy)),
          this.exportCellStr(this.exportExcelActionByLabel(item.actionByUser)),
          this.exportFormDataCell(formDataVal),
        ];
      });
      return [header, ...body];
    },
    /** 解析列表 API 首包：total、數據庫配置的 maxExportRows、是否超出導出上限（與存儲過程一致） */
    parseOpLogListSummary(data, listLengthFallback) {
      const d = data || {};
      let total = Number(d.total ?? d.Total ?? d.totalCount ?? d.TotalCount ?? 0) || 0;
      if (total <= 0 && listLengthFallback != null && listLengthFallback > 0) {
        total = listLengthFallback;
      }
      const rawMax = d.maxExportRows ?? d.MaxExportRows;
      let maxExportRows = null;
      if (rawMax !== null && rawMax !== undefined && rawMax !== "") {
        const n = Math.floor(Number(rawMax));
        if (Number.isFinite(n) && n > 0) maxExportRows = n;
      }

      const rawEx = d.exportLimitExceeded ?? d.ExportLimitExceeded;
      let exportLimitExceeded = false;
      if (rawEx !== undefined && rawEx !== null && rawEx !== "") {
        exportLimitExceeded =
          rawEx === true
          || rawEx === 1
          || rawEx === "1"
          || String(rawEx).toLowerCase() === "true";
      } else if (maxExportRows != null) {
        exportLimitExceeded = total > maxExportRows;
      }
      return { total, maxExportRows, exportLimitExceeded };
    },
    /** 後端若回傳「已有導出任務」類訊息，顯示排隊提示彈窗（可依實際 API 擴充關鍵字） */
    isExportConflictResponse(res) {
      const code = Number(res?.statusCode ?? res?.StatusCode);
      if (code === 409 || code === 429) return true;
      const parts = [...(res?.msg || []), ...(res?.systemErrorMsg || [])].map((x) => String(x || ""));
      const s = parts.join(" ").toLowerCase();
      if (!s.trim()) return false;
      return (
        /currently exporting|other data is exporting|export.*in progress|concurrent export|請稍後再試|请稍后再试|請稍後再試|其他數據正在導出|其他数据正在导出|已有其他.*導出|已有其他.*导出|please wait.*complete|try again later/i.test(s)
      );
    },
    startExportDownload() {
      if (!this.canQuery) return;
      if (oplogExportHud.active) {
        this.exportQueueModalVisible = true;
        return;
      }
      if (this.downloading) return;
      const companyId = this.filters.companyId || this.companyId;
      const snapshot = {
        mode: this.mode,
        filters: {
          companyId: String(companyId || ""),
          logType: this.mode,
          actionByUser: this.filters.actionByUser,
          action: this.filters.action,
          formType: this.filters.formType,
          fromDate: this.filters.fromDate,
          toDate: this.filters.toDate,
        },
      };
      empDownloadHudClose();
      oplogExportHudStartJob(this.exportToastCompanyLine);
      this.downloading = true;
      this.exportCheckingLimit = true;
      this.$nextTick(() => {
        this.runExportDownloadJob(snapshot).finally(() => {
          oplogExportHudFinishJob();
          this.downloading = false;
          this.exportCheckingLimit = false;
        });
      });
    },
    async runExportDownloadJob(snapshot) {
      const f = snapshot.filters || {};
      const companyId = f.companyId || this.companyId;
      const exportMode = snapshot.mode;
      let exportLeaseToken = null;
      try {
        const basePayload = {
          companyId: Number(companyId) || companyId,
          actionByUser: this.parseActionByUserFilter(f.actionByUser),
          action: f.action || null,
          formType: f.formType || null,
          fromDate: f.fromDate ? `${f.fromDate} 00:00:00` : null,
          toDate: f.toDate ? `${f.toDate} 23:59:59` : null,
          uiLangId: this.getCurrentLangId(),
          opLogBulkExport: true,
        };
        const pageSize = 200;
        let pageNumber = 1;
        const rows = [];
        const first = exportMode === "FORM"
          ? await LogService.GetLogFormDataList({
            ...basePayload,
            pageNumber,
            pageSize,
            includeFormData: true,
          })
          : await LogService.GetLogUserActionList({
            ...basePayload,
            pageNumber,
            pageSize,
            includeUserActionContext: true,
          });
        if (!first || !this.opLogApiOk(first)) {
          oplogExportHudHideToast();
          if (this.isExportConflictResponse(first)) {
            this.exportQueueModalVisible = true;
            return;
          }
          this.__opLogDevLog("[OpLog page] export aborted on first page", { exportMode, totalHint: first?.data });
          this.applyExportFailure(first);
          return;
        }
        const d0 = first.data || {};
        exportLeaseToken = d0.exportLeaseToken || d0.ExportLeaseToken || null;
        const list0 = d0.items || d0.list || d0 || [];
        const list0Len = Array.isArray(list0) ? list0.length : 0;
        const { total, maxExportRows, exportLimitExceeded } = this.parseOpLogListSummary(d0, list0Len);
        if (exportLimitExceeded) {
          oplogExportHudHideToast();
          if (maxExportRows != null) this.opLogMaxExportRows = maxExportRows;
          this.exportLimitModalVisible = true;
          return;
        }

        this.exportCheckingLimit = false;
        oplogExportHudEndPreparing();

        if (Array.isArray(list0)) {
          list0.forEach((r) => rows.push(this.normalizeItem(r, exportMode)));
        }
        const maxPages = total > 0 ? Math.ceil(total / pageSize) : 1;
        while (pageNumber < maxPages) {
          pageNumber += 1;
          const pagePayload = {
            ...basePayload,
            pageNumber,
            pageSize,
            ...(exportLeaseToken ? { opLogExportLeaseToken: exportLeaseToken } : {}),
          };
          const res = exportMode === "FORM"
            ? await LogService.GetLogFormDataList({
              ...pagePayload,
              includeFormData: true,
            })
            : await LogService.GetLogUserActionList({
              ...pagePayload,
              includeUserActionContext: true,
            });
          if (!res || !this.opLogApiOk(res)) {
            oplogExportHudHideToast();
            if (this.isExportConflictResponse(res)) {
              this.exportQueueModalVisible = true;
              return;
            }
            this.__opLogDevLog("[OpLog page] export aborted mid-pagination", {
              exportMode,
              pageNumber,
              maxPages,
              rowsLoaded: rows.length,
            });
            this.applyExportFailure(res);
            return;
          }
          const data = res.data || {};
          const list = data.items || data.list || data || [];
          if (Array.isArray(list)) {
            list.forEach((r) => rows.push(this.normalizeItem(r, exportMode)));
          }
        }
        const aoa = this.buildExportSheet(rows, exportMode);
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        const sheetName = String(this.$t("lbl_EMPBE_OpLog_ExportSheetName") || "Action Log").slice(0, 31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        const fname = `action_log_${this.formatExportTimestamp(new Date())}.xlsx`;
        XLSX.writeFile(wb, fname);
        this.logOperationLogExportToFormData(snapshot, {
          basePayload,
          total,
          maxExportRows,
          exportedRowCount: rows.length,
          fileName: fname,
        });
        await new Promise((r) => setTimeout(r, 2000));
        oplogExportHudHideToast();
      } catch (e) {
        this.__opLogDevLog("[OpLog page] export unexpected error (client / xlsx / memory)", e);
        this.applyExportFailure(e);
      } finally {
        if (exportLeaseToken) {
          const t = exportLeaseToken;
          exportLeaseToken = null;
          LogService.ReleaseOpLogExportLease({ opLogExportLeaseToken: t }).catch(() => {});
        }
      }
    },
    /** 导出审计：筛选项空值 → All；有值则保留原码 */
    toOpLogExportAuditFilterToken(raw) {
      return isOpLogExportFilterAll(raw) ? OPLOG_EXPORT_FILTER_ALL : String(raw).trim();
    },
    resolveOpLogExportUserLabel(rawUserId) {
      if (isOpLogExportFilterAll(rawUserId)) {
        return String(this.$t("lbl_EMPBE_OpLog_AllUsers") || OPLOG_EXPORT_FILTER_ALL);
      }
      const idStr = String(rawUserId).trim();
      return this.userMap.get(idStr) || `ID ${idStr}`;
    },
    resolveOpLogExportActionLabel(rawAction, mode) {
      if (isOpLogExportFilterAll(rawAction)) {
        return String(this.$t("lbl_EMPBE_OpLog_AllActions") || OPLOG_EXPORT_FILTER_ALL);
      }
      const code = String(rawAction).trim();
      const rows = mode === "USER_ACTION" ? this.metaUserActions : this.metaFormActions;
      const hit = (rows || []).find((r) => String(r.code || "").trim() === code);
      if (hit && hit.displayLabel) return hit.displayLabel;
      return this.actionDisplayLabelForMode(code, mode) || code;
    },
    resolveOpLogExportTypeLabel(rawType, mode) {
      if (isOpLogExportFilterAll(rawType)) {
        return mode === "USER_ACTION"
          ? String(this.$t("lbl_EMPBE_OpLog_AllLogActionTypes") || OPLOG_EXPORT_FILTER_ALL)
          : String(this.$t("lbl_EMPBE_OpLog_AllTypes") || OPLOG_EXPORT_FILTER_ALL);
      }
      const code = String(rawType).trim();
      const rows = mode === "USER_ACTION" ? this.metaUserActionLogTypes : this.metaFormTypes;
      const hit = (rows || []).find((r) => String(r.code || "").trim() === code);
      if (hit && hit.displayLabel) return hit.displayLabel;
      return this.humanizeOpLogCodeKey(code) || code;
    },
    formatOpLogExportDateRange(fromDate, toDate) {
      const from = fromDate ? String(fromDate).trim() : "";
      const to = toDate ? String(toDate).trim() : "";
      if (from && to) return `${from} ~ ${to}`;
      if (from) return from;
      if (to) return to;
      return OPLOG_EXPORT_FILTER_ALL;
    },
    langIdToOpLogLocaleTag(langId) {
      const n = Number(langId);
      if (n === 950) return "zh-HK";
      if (n === 1252) return "en";
      return "zh-CN";
    },
    /**
     * 组装导出审计 FormJSON（可读摘要 + 机器码；API 实际传参另存 apiPayloadSentToApi）。
     */
    buildOperationLogExportAuditFormData(snapshot, meta) {
      const u = this.$store?.state?.authentication?.user || {};
      const nav = typeof window !== "undefined" && window.navigator ? window.navigator : {};
      const mode = snapshot?.mode || this.mode;
      const f = snapshot?.filters || {};
      const rawApi = { ...(meta?.basePayload || {}) };
      const langId = this.getCurrentLangId();
      const targetCompanyId =
        rawApi.companyId != null ? rawApi.companyId : (f.companyId || null);
      const logTypeLabel =
        mode === "USER_ACTION"
          ? String(this.$t("lbl_EMPBE_OpLog_LogType_UserAction") || "")
          : String(this.$t("lbl_EMPBE_OpLog_LogType_Form") || "");

      const uiFilters = {
        companyId: f.companyId != null ? String(f.companyId) : "",
        logType: f.logType || mode,
        actionByUser: this.toOpLogExportAuditFilterToken(f.actionByUser),
        action: this.toOpLogExportAuditFilterToken(f.action),
        formType: this.toOpLogExportAuditFilterToken(f.formType),
        fromDate: f.fromDate ? String(f.fromDate) : "",
        toDate: f.toDate ? String(f.toDate) : "",
      };

      const apiPayloadUsedForExport = {
        companyId: rawApi.companyId,
        logMode: mode,
        actionByUser:
          rawApi.actionByUser == null ? OPLOG_EXPORT_FILTER_ALL : rawApi.actionByUser,
        action: rawApi.action == null ? OPLOG_EXPORT_FILTER_ALL : rawApi.action,
        formType: rawApi.formType == null ? OPLOG_EXPORT_FILTER_ALL : rawApi.formType,
        fromDate: rawApi.fromDate,
        toDate: rawApi.toDate,
        uiLangId: rawApi.uiLangId,
        opLogBulkExport: rawApi.opLogBulkExport === true,
      };

      const filterSummary = {
        logType: logTypeLabel || mode,
        user: this.resolveOpLogExportUserLabel(f.actionByUser),
        action: this.resolveOpLogExportActionLabel(f.action, mode),
        type: this.resolveOpLogExportTypeLabel(f.formType, mode),
        dateRange: this.formatOpLogExportDateRange(f.fromDate, f.toDate),
      };

      return {
        event: "OperationLogExportExcel",
        logMode: mode,
        logModeLabel: logTypeLabel || mode,
        filterSummary,
        uiFilters,
        apiPayloadUsedForExport,
        apiPayloadSentToApi: rawApi,
        listTotalCount: meta?.total,
        exportedRowCount: meta?.exportedRowCount,
        maxExportRowsAtExport: meta?.maxExportRows != null ? meta.maxExportRows : null,
        fileName: meta?.fileName || null,
        targetCompanyId,
        targetCompany: {
          id: targetCompanyId,
          displayName: String(this.companyDisplayName || "").trim() || null,
          labelLine: this.exportToastCompanyLine || null,
        },
        exporter: {
          accountUserId: this.getCurrentAccountUserId(),
          userType: u.userType || u.UserType || null,
          loginEmpCompanyId: u.empCompanyId ?? u.EmpCompanyId ?? null,
          simulatedSupplierView: !!this.simulatedSupplier,
        },
        routePath: this.$route?.path || null,
        pageUrl: typeof window !== "undefined" ? window.location?.href : null,
        langId,
        uiLocale: this.langIdToOpLogLocaleTag(langId),
        userAgent: nav.userAgent || null,
        exportedAtUtc: new Date().toISOString(),
      };
    },
    /**
     * 导出成功後寫入 dbo.log_form_Data（與其他表單日誌同源，可查 RID / FormType 等）。
     * CreateBy、ActionByUser 由後端 JWT 寫入；此處 FormJSON 記錄篩選參數、目標公司、導出行數與下載人上下文。
     */
    logOperationLogExportToFormData(snapshot, meta) {
      try {
        const formData = this.buildOperationLogExportAuditFormData(snapshot, meta);
        let json = JSON.stringify(formData);
        const maxJson = 32000;
        if (json.length > maxJson) {
          json = `${json.slice(0, maxJson - 40)}…"truncated":true}`;
        }
        const formIdNum = Number(formData.targetCompanyId);
        LogService.LogFormData({
          FormId: Number.isFinite(formIdNum) && formIdNum > 0 ? formIdNum : 0,
          Action: "ExportExcel",
          FormType: "OperationLogExport",
          FormJSON: json,
          ErrorLog: null,
        });
      } catch (e) {
        this.__opLogDevLog("[OpLog page] log_form_Data export audit failed", e);
      }
    },
    /**
     * 導出失敗（含逾時）：與需求一致，統一走右下角紅色 empDownloadHud（#197.3）。
     */
    applyExportFailure(_ctx) {
      oplogExportHudHideToast();
      empDownloadHudOpen({
        variant: "error",
        title: this.$t("lbl_EMPBE_OpLog_ExportFailToastTitle"),
        subtitle: this.$t("lbl_EMPBE_OpLog_ExportFailToastSubtitle"),
        contextLabel: this.exportToastCompanyLine,
        autoCloseMs: 10_000,
      });
    },
    async refresh(forcePageNumber) {
      const companyId = this.filters.companyId || this.companyId;
      if (!companyId) {
        this.items = [];
        this.total = 0;
        this.hint = this.$t("lbl_EMPBE_OpLog_MissingCompanyId");
        return;
      }

      const seq = ++this.refreshSeq;
      this.loading = true;
      this.hint = "";
      try {
        const pageNumber =
          Number.isFinite(Number(forcePageNumber)) && Number(forcePageNumber) > 0
            ? Number(forcePageNumber)
            : this.page;
        const payload = {
          companyId: Number(companyId) || companyId,
          actionByUser: this.parseActionByUserFilter(this.filters.actionByUser),
          action: this.filters.action || null,
          formType: this.filters.formType || null,
          fromDate: this.filters.fromDate ? `${this.filters.fromDate} 00:00:00` : null,
          toDate: this.filters.toDate ? `${this.filters.toDate} 23:59:59` : null,
          uiLangId: this.getCurrentLangId(),
          pageNumber,
          pageSize: this.pageSize,
          ...(this.mode === "FORM" ? { includeFormData: true } : {}),
          ...(this.mode === "USER_ACTION" ? { includeUserActionContext: true } : {}),
        };

        const res = this.mode === "FORM"
          ? await LogService.GetLogFormDataList(payload)
          : await LogService.GetLogUserActionList(payload);

        // 丢弃过期响应：避免用户快速切换分页/筛选时，旧请求后返回覆盖新请求的数据
        if (seq !== this.refreshSeq) {
          return;
        }

        if (!res || typeof res !== "object" || !this.opLogApiOk(res)) {
          this.__opLogDevLog("[OpLog page] refresh list unsuccessful", {
            mode: this.mode,
            pageNumber,
            statusCode: res?.statusCode,
            msg: res?.msg,
            systemErrorMsg: res?.systemErrorMsg,
          });
          // Backend might not be ready; keep UX clear but non-blocking
          this.items = [];
          this.total = 0;
          const msg =
            (res && (res.systemErrorMsg?.[0] || res.msg?.[0]))
            || this.$t("lbl_EMPBE_OpLog_QueryFailed");
          this.hint = msg;
          return;
        }

        // Allow flexible response shape
        const data = res.data || {};
        const list = data.items || data.list || data || [];
        const { total, maxExportRows } = this.parseOpLogListSummary(
          data,
          Array.isArray(list) ? list.length : 0
        );
        if (maxExportRows != null) this.opLogMaxExportRows = maxExportRows;

        // 后端已默认过滤未知操作者并默认不返回大字段，这里不再做前端过滤
        this.items = Array.isArray(list) ? list.map((row) => this.normalizeItem(row)) : [];
        this.total = total;
        if (!this.items.length && !this.hint) {
          this.hint = this.$t("lbl_EMPBE_OpLog_NoMatch");
        }

        if (seq === this.refreshSeq) {
          await this.enrichMissingOperatorProfiles();
        }

        // formType 下拉改为从表的元数据加载（见 loadMeta），这里不再用“当前页 items”拼出来
      } catch (e) {
        this.__opLogDevLog("[OpLog page] refresh list exception", e);
        this.items = [];
        this.total = 0;
        this.hint = e?.message || "查询失败";
      } finally {
        this.loading = false;
      }
    },
    /**
     * 用户动作日志：操作者账号 ID。proc / API 可能输出 UserId，也可能与表单日志统一为 ActionByUser；
     * JSON 序列化可能是 PascalCase、camelCase 或 dynamic 字典键不完全一致，故做多键兜底。
     */
    pickUserActionOperatorAccountId(r) {
      const raw =
        r.UserId
        ?? r.userId
        ?? r.UserID
        ?? r.ActionByUser
        ?? r.actionByUser
        ?? r.accountUserId
        ?? r.AccountUserId;
      if (raw === null || raw === undefined || raw === "") return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : raw;
    },
    normalizeItem(row, rowMode) {
      const mode = rowMode != null ? rowMode : this.mode;
      const r = row || {};
      if (mode === "USER_ACTION") {
        const uid = this.pickUserActionOperatorAccountId(r);
        return {
          rid: r.RID ?? r.rid ?? null,
          formId: null,
          action: r.Action ?? r.action ?? null,
          formType: r.LogActionType ?? r.logActionType ?? null,
          displayFormType: r.DisplayFormType ?? r.displayFormType ?? null,
          displayAction: r.DisplayAction ?? r.displayAction ?? null,
          formData: null,
          createdDate: r.CreatedDate ?? r.createdDate ?? null,
          createdBy: r.EmpCompanyId ?? r.empCompanyId ?? null,
          actionByUser: uid,
          url: r.Url ?? r.url ?? null,
          ip: r.IP ?? r.ip ?? null,
          bAgent: r.BAgent ?? r.bAgent ?? null,
          referral: r.Referral ?? r.referral ?? null,
          domId: r.DOM_ID ?? r.domId ?? null,
          domClass: r.DOM_Class ?? r.domClass ?? null,
        };
      }
      const au = r.ActionByUser ?? r.actionByUser ?? null;
      return {
        rid: r.RID ?? r.rid ?? r.Id ?? r.id ?? null,
        formId: r.FormId ?? r.formId ?? null,
        action: r.Action ?? r.action ?? null,
        formType: r.FormType ?? r.formType ?? null,
        displayFormType: r.DisplayFormType ?? r.displayFormType ?? null,
        displayAction: r.DisplayAction ?? r.displayAction ?? null,
        formData: r.FormData ?? r.formData ?? r.FormJSON ?? r.formJson ?? null,
        createdDate: r.CreatedDate ?? r.createdDate ?? null,
        createdBy: r.CreatedBy ?? r.createdBy ?? null,
        errorLog: r.ErrorLog ?? r.errorLog ?? null,
        actionByUser: au,
      };
    },
    openDetail(item) {
      this.detailMode = this.mode;
      this.selected = item;
      this.detailModalVisible = true;
      if (this.mode === "FORM") {
        this.loadFormDetailByRid(item?.rid);
      } else if (this.mode === "USER_ACTION") {
        this.loadUserActionDetailByRid(item?.rid);
      }
    },
    async loadFormDetailByRid(rid) {
      const companyId = this.filters.companyId || this.companyId;
      const r = Number(rid) || 0;
      if (!companyId || r <= 0) return;
      try {
        const res = await LogService.GetLogFormDataList({
          companyId: Number(companyId) || companyId,
          rid: r,
          pageNumber: 1,
          pageSize: 1,
          includeFormData: true,
          uiLangId: this.getCurrentLangId(),
          // 详情允许看到未知操作者记录本身（但列表层默认已过滤）
          hideUnknownOperator: false,
        });
        if (!res || !this.opLogApiOk(res)) return;
        const data = res.data || {};
        const list = data.items || data.list || data || [];
        const row = Array.isArray(list) ? list[0] : null;
        if (!row) return;
        const normalized = this.normalizeItem(row);
        if (this.selected && String(this.selected.rid) === String(r)) {
          this.selected = { ...this.selected, ...normalized };
        }
      } catch (e) {
        this.__opLogDevLog("[OpLog page] loadFormDetailByRid exception", { rid: r }, e);
      }
    },
    async loadUserActionDetailByRid(rid) {
      const companyId = this.filters.companyId || this.companyId;
      const r = Number(rid) || 0;
      if (!companyId || r <= 0) return;
      try {
        const res = await LogService.GetLogUserActionList({
          companyId: Number(companyId) || companyId,
          rid: r,
          pageNumber: 1,
          pageSize: 1,
          includeUserActionContext: true,
          uiLangId: this.getCurrentLangId(),
          hideUnknownOperator: false,
        });
        if (!res || !this.opLogApiOk(res)) return;
        const data = res.data || {};
        const list = data.items || data.list || data || [];
        const row = Array.isArray(list) ? list[0] : null;
        if (!row) return;
        const normalized = this.normalizeItem(row);
        if (this.selected && String(this.selected.rid) === String(r)) {
          this.selected = { ...this.selected, ...normalized };
        }
      } catch (e) {
        this.__opLogDevLog("[OpLog page] loadUserActionDetailByRid exception", { rid: r }, e);
      }
    },
    formatDate(value) {
      if (!value) return "";
      return String(value);
    },
    previewJson(value) {
      if (!value) return "";
      const raw = typeof value === "string" ? value : JSON.stringify(value);
      const trimmed = raw.replace(/\s+/g, " ").trim();
      if (trimmed.length <= TABLE_JSON_PREVIEW_MAX) return trimmed;
      return trimmed.slice(0, TABLE_JSON_PREVIEW_MAX) + "…";
    },
    prettyJson(value) {
      if (!value) return "";
      try {
        const obj = typeof value === "string" ? JSON.parse(value) : value;
        return JSON.stringify(obj, null, 2);
      } catch (e) {
        return typeof value === "string" ? value : JSON.stringify(value, null, 2);
      }
    },
  },
};
</script>

<style scoped>
.oplog__filters {
  padding: 12px 16px;
}
.oplog__filter-field {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
}
.oplog__filters .oplog__filter-control {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}
.oplog__label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 6px;
}
.oplog__hint {
  margin-top: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
}
.oplog__pager {
  padding: 12px 0;
}
.oplog__table {
  table-layout: fixed;
  width: 100%;
}
.oplog__col-tight {
  width: 10%;
  max-width: 10%;
  vertical-align: top;
  word-break: break-word;
}
.oplog__col-formdata {
  width: 16.5%;
  max-width: 16.5%;
  vertical-align: top;
  word-break: break-word;
}
.oplog__col-formdata .oplog__json-preview {
  display: block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.oplog__json {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  background: #f7f7f7;
  border: 1px solid #eee;
  border-radius: 6px;
  margin: 8px 0 0;
  padding: 16px 20px 16px 24px;
  max-height: 420px;
  overflow: auto;
  font-size: 12px;
}
/* modal-body 内 .row 负边距会抵消默认 padding，导致整页文字贴左 */
::v-deep #oplog-detail-modal .oplog-detail-modal-body {
  padding: 1.5rem 1.75rem 1.75rem 2rem;
  overflow-x: hidden;
}
::v-deep #oplog-detail-modal .oplog-detail-modal-body .row {
  margin-left: 0;
  margin-right: 0;
}
::v-deep #oplog-detail-modal .oplog-detail-modal-body [class*='col-'] {
  padding-left: 0;
  padding-right: 0;
}
.oplog__json-preview {
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgba(0, 0, 0, 0.65);
}
.oplog__operator {
  display: inline-block;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}
.oplog__operator ::v-deep .oplog__op-tag {
  font-weight: 500;
}
.oplog__operator ::v-deep .oplog__op-tag--inactive {
  color: #dc3545;
}
.oplog__operator ::v-deep .oplog__op-tag--root {
  color: #d4a017;
}
.oplog__operator ::v-deep .oplog__op-tag--backend {
  color: #28a745;
}
.oplog__user-filter-dropdown {
  max-width: 100%;
}
.oplog__user-filter-dropdown ::v-deep > .btn,
.oplog__user-filter-dropdown ::v-deep > .btn.dropdown-toggle {
  width: 100%;
  max-width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  min-height: 31px;
  padding: 0.25rem 0.5rem;
  background-color: #fff !important;
  background-image: none !important;
  border-color: #ced4da !important;
  color: #495057 !important;
  box-shadow: none !important;
}
.oplog__user-filter-dropdown ::v-deep > .btn:hover,
.oplog__user-filter-dropdown ::v-deep > .btn:focus,
.oplog__user-filter-dropdown ::v-deep > .btn:active,
.oplog__user-filter-dropdown.show ::v-deep > .btn,
.oplog__user-filter-dropdown ::v-deep.show > .btn {
  background-color: #fff !important;
  background-image: none !important;
  border-color: #adb5bd !important;
  color: #495057 !important;
  box-shadow: none !important;
}
.oplog__user-filter-btn-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #495057;
}
.oplog__user-filter-btn-label ::v-deep .oplog__user-filter-plain {
  color: #495057;
}
.oplog__user-filter-btn-label ::v-deep .oplog__op-tag--inactive {
  color: #dc3545;
}
.oplog__user-filter-btn-label ::v-deep .oplog__op-tag--root {
  color: #d4a017;
}
.oplog__user-filter-btn-label ::v-deep .oplog__op-tag--backend {
  color: #28a745;
}
::v-deep .oplog__user-filter-menu {
  max-height: 320px;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  min-width: 100%;
  max-width: 100%;
}
::v-deep .oplog__user-filter-menu .dropdown-item {
  font-size: 13px;
  color: #212529 !important;
  background-color: transparent !important;
  max-width: 100%;
  padding-right: 1rem;
}
::v-deep .oplog__user-filter-menu .dropdown-item:hover,
::v-deep .oplog__user-filter-menu .dropdown-item:focus,
::v-deep .oplog__user-filter-menu .dropdown-item.active {
  color: #212529 !important;
  background-color: #f1f3f5 !important;
}
.oplog__user-filter-item-text {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
::v-deep .oplog__user-filter-menu .dropdown-item .oplog__op-tag--inactive {
  color: #dc3545;
}
::v-deep .oplog__user-filter-menu .dropdown-item .oplog__op-tag--root {
  color: #d4a017;
}
::v-deep .oplog__user-filter-menu .dropdown-item .oplog__op-tag--backend {
  color: #28a745;
}
/* 搜索 / 导出 / 重设 / 预览同一横排；宽度不够时横向滚动，不折行 */
.oplog__filter-actions-bar {
  max-width: 100%;
}
.oplog__filter-actions-tail {
  flex-shrink: 0;
}
</style>

